import { FormEvent, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { useAuthUserData, useUpdateAuthUserData } from '../../../authUserProvider'
import { Banner, Button, Card, Field, FieldGrid, Input, useToast } from 'ui'
import { apiErrorMessage, UserProfile } from './shared'
import s from './styles.module.scss'

const NAME_MIN = 2
const NAME_MAX = 100

interface Props {
    profile: UserProfile
    onChange: (patch: Partial<UserProfile>) => void
}

/** Name + phone, written to Cognito and mirrored to dashboard_users. */
export const PersonalDetailsCard = ({ profile, onChange }: Props) => {
    const authUser = useAuthUserData()
    const updateAuthUser = useUpdateAuthUserData()
    const { toast } = useToast()

    const [name, setName] = useState(profile.name || '')
    const [phone, setPhone] = useState(profile.phone || '')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const trimmedName = name.replace(/\s+/g, ' ').trim()
    const dirty = trimmedName !== (profile.name || '') || phone.trim() !== (profile.phone || '')
    const nameInvalid = trimmedName.length > 0 && (trimmedName.length < NAME_MIN || trimmedName.length > NAME_MAX)

    const reset = () => {
        setName(profile.name || '')
        setPhone(profile.phone || '')
        setError(null)
    }

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault()
        if (saving) return
        setError(null)

        if (trimmedName.length < NAME_MIN || trimmedName.length > NAME_MAX) {
            setError(`Name must be between ${NAME_MIN} and ${NAME_MAX} characters.`)
            return
        }

        const updates: { name?: string; phone?: string } = {}
        if (trimmedName !== (profile.name || '')) updates.name = trimmedName
        if (phone.trim() !== (profile.phone || '')) updates.phone = phone.trim()
        if (!Object.keys(updates).length) return

        setSaving(true)
        try {
            const response = await api({ url: '/profile-update', data: updates })
            if (!response.data?.success) throw new Error(response.data?.error || 'Update failed')

            const applied = response.data.applied || {}
            const nextName = applied.name ?? profile.name
            const nextPhone = applied.phone ?? profile.phone
            onChange({ name: nextName, phone: nextPhone })
            setName(nextName)
            setPhone(nextPhone)

            // Keep the sidebar (and any other reader of the auth user) in step
            // without forcing a re-login: both the context and the localStorage
            // copy `Main` rehydrates from.
            if (applied.name && authUser) {
                const nextUser = { ...authUser, name: applied.name }
                updateAuthUser(nextUser)
                try {
                    window.localStorage.setItem('user', JSON.stringify(nextUser))
                } catch {
                    // best-effort
                }
            }

            toast({
                title: 'Profile updated',
                description: response.data.warning
                    ? 'Saved to your login account. The dashboard user record will catch up on the next sync.'
                    : 'Your details have been saved.',
                tone: response.data.warning ? 'warn' : 'good',
            })
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Could not save your details.'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Card title="Personal details" subtitle="Your name is shown in the sidebar and on records you create.">
            <form className={s.form} onSubmit={handleSubmit} noValidate>
                {error && (
                    <Banner tone="bad" title="Couldn't save">
                        {error}
                    </Banner>
                )}
                <FieldGrid minColumn={240}>
                    <Field label="Full name" required error={nameInvalid ? `Between ${NAME_MIN} and ${NAME_MAX} characters` : undefined}>
                        {(id) => (
                            <Input
                                id={id}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoComplete="name"
                                maxLength={NAME_MAX}
                                invalid={nameInvalid}
                                disabled={saving}
                            />
                        )}
                    </Field>
                    <Field label="Phone number" optional hint="UK numbers are stored in international format (+44…).">
                        {(id) => (
                            <Input
                                id={id}
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                autoComplete="tel"
                                placeholder="07123 456789"
                                disabled={saving}
                            />
                        )}
                    </Field>
                </FieldGrid>
                <div className={s.formActions}>
                    {dirty && <p className={s.formNote}>You have unsaved changes.</p>}
                    <Button type="button" onClick={reset} disabled={!dirty || saving}>
                        Reset
                    </Button>
                    <Button type="submit" variant="primary" loading={saving} disabled={!dirty || nameInvalid}>
                        Save changes
                    </Button>
                </div>
            </form>
        </Card>
    )
}
